import FinanceRefundDialog from './FinanceRefundDialog';
import FinanceReadyButton from './FinanceReadyButton';
import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from 'primereact/button';
import { Column } from 'primereact/column';
import { DataTable } from 'primereact/datatable';
import { Dropdown } from 'primereact/dropdown';
import { InputText } from 'primereact/inputtext';
import { Tag } from 'primereact/tag';
import { handleGetRequest } from '../../service/GetTemplate';
import { handlePatchRequest } from '../../service/PatchTemplate';
import { handlePostRequest } from '../../service/PostTemplate';
import { endPoints } from '../../Config';
import usePreferredLanguage from '../../utilities/usePreferredLanguage';
import { translateUiText } from '../../utilities/siteTranslations';

const refundStatusOptions = [
    { label: 'All in Finance', value: 'finance' },
    { label: 'All Refunds', value: '' },
    { label: 'Pending Finance Review', value: 'pending_finance_review' },
    { label: 'Ready for Bank Upload', value: 'ready_for_bank_upload' },
    { label: 'Revision', value: 'revision' },
    { label: 'Exported', value: 'exported' },
    { label: 'Submitted to Bank', value: 'submitted_to_bank' },
    { label: 'Approved', value: 'approved' },
    { label: 'Rejected', value: 'rejected' },
    { label: 'Pendientes del flujo manual anterior', value: 'manual_pending' },
    { label: 'Rechazo en curso', value: 'rejection_pending' }
];

const statusSeverity = (status) => {
    if (status === 'customer_notified' || status === 'approved') return 'success';
    if (status === 'rejected') return 'danger';
    if (status === 'revision') return 'warning';
    if (status === 'exported' || status === 'submitted_to_bank') return 'info';
    return 'warning';
};

const formatStatus = (status) =>
    ({ manual_pending: 'Pendiente de preparar', rejection_pending: 'Rechazo en curso' }[status]) || String(status || '-')
        .split('_')
        .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
        .join(' ');

const formatRefundAccountType = (accountType) => {
    if (accountType === 'Ahorro' || accountType === 'Corriente') return accountType;
    if (accountType === '04') return 'Ahorro';
    if (accountType === '05') return 'Ahorro';
    if (accountType === '03') return 'Corriente';
    return accountType || '-';
};

const formatAdminUserName = (user) => {
    if (!user) return null;
    if (typeof user === 'string') return user;
    return (
        user.name ||
        `${user.firstName || ''} ${user.lastName || ''}`.trim() ||
        user.email ||
        null
    );
};

const formatRefundAuditText = (audit = {}) => {
    const actionKey = String(audit.action || '').trim().toLowerCase();
    const statusKey = String(audit.status || '').trim().toLowerCase();

    const financeLabels = {
        finance_mark_ready: 'Listo para exportar a TXT',
        finance_start_manual: 'Gestión manual pendiente',
        finance_complete_manual: 'Pago manual registrado',
        finance_reopen_rejected: 'Rechazo corregido: pendiente de revisión',
        finance_request_correction: 'Corrección de datos solicitada'
    };
    if (financeLabels[actionKey]) return `${financeLabels[actionKey]}${audit.note ? `: ${audit.note}` : ''}`;
    if (actionKey.startsWith('manual_payment_notification_')) {
        const labels = { sent: 'Notificación aceptada por el proveedor', failed: 'Notificación fallida: el pago sigue registrado', suppressed: 'Notificación desactivada' };
        return labels[actionKey.replace('manual_payment_notification_', '')] || 'Notificación de pago manual';
    }
    if (statusKey === 'rejected' || actionKey.includes('reject')) {
        return audit.note ? `Refund not completed: ${audit.note}` : 'Refund not completed';
    }
    if (statusKey === 'approved' || actionKey.includes('approved')) {
        return audit.note ? `Refund completed: ${audit.note}` : 'Refund completed';
    }
    if (statusKey === 'customer_notified' || actionKey.includes('notified')) {
        return audit.note ? `Customer notified: ${audit.note}` : 'Customer notified';
    }
    if (statusKey === 'refund_in_process') {
        return audit.note ? `Refund in process: ${audit.note}` : 'Refund in process';
    }

    return null;
};

const isRefundOutcomeAudit = (audit = {}) => {
    const actionKey = String(audit.action || '').trim().toLowerCase();
    const statusKey = String(audit.status || '').trim().toLowerCase();
    return (
        actionKey.startsWith('finance_') || actionKey.startsWith('manual_payment_notification_') ||
        ['approved', 'rejected', 'customer_notified', 'refund_in_process'].includes(statusKey) ||
        actionKey.includes('approved') ||
        actionKey.includes('reject') ||
        actionKey.includes('notified')
    );
};

const getRefundCommentsForDisplay = (refund) => {
    const comments = Array.isArray(refund?.comments) ? refund.comments : [];
    const reason = String(refund?.reason || '').trim();
    const hasReasonInComments = comments.some((comment) => String(comment?.text || '').trim() === reason);
    const auditComments = Array.isArray(refund?.audit)
        ? refund.audit
            .filter(isRefundOutcomeAudit)
            .map((entry) => ({
                text: formatRefundAuditText(entry),
                createdBy: entry.updatedBy || null,
                createdAt: entry.createdAt,
                refundAudit: true
            }))
            .filter((entry) => entry.text)
        : [];

    const displayComments = [...comments];
    if (reason && !hasReasonInComments) {
        displayComments.push({
            text: reason,
            createdBy: null,
            legacyReason: true
        });
    }
    return [...displayComments, ...auditComments];
};

const toAmount = (value) => {
    const amount = Number(value);
    return Number.isFinite(amount) ? amount : 0;
};

const normalizeSearchText = (value) =>
    String(value || '')
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .toLowerCase()
        .trim();

const panamaDateFormatter = new Intl.DateTimeFormat('en-US', {
    timeZone: 'America/Panama',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit'
});

const toDateKey = (dateValue) => {
    const date = new Date(dateValue);
    if (Number.isNaN(date.getTime())) return '';
    const parts = panamaDateFormatter.formatToParts(date);
    const getPart = (type) => parts.find((part) => part.type === type)?.value || '';
    return `${getPart('year')}-${getPart('month')}-${getPart('day')}`;
};

const getCurrentMonthRange = () => {
    const today = toDateKey(new Date());
    return {
        fromDate: `${today.slice(0, 7)}-01`,
        toDate: today
    };
};

const RefundQueue = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const location = useLocation();
    const [searchParams, setSearchParams] = useSearchParams();
    const preferredLanguage = usePreferredLanguage();
    const t = (value) => translateUiText(value, preferredLanguage);
    const currentAdminUser = JSON.parse(localStorage.getItem('userDetails') || '{}')?.data?.adminUser || {};
    const currentRoleUid = currentAdminUser?.role?.uid;
    const canOpenComplaintDetail = ['admin', 'superAdmin', 'finance'].includes(currentRoleUid);
    const [refunds, setRefunds] = useState([]);
    const [financeDialog, setFinanceDialog] = useState(null);
    const [financeNotice, setFinanceNotice] = useState('');
    const requestedStatus = searchParams.get('status');
    const initialStatusFilter = requestedStatus === 'customer_notified'
        ? 'approved'
        : (requestedStatus || 'finance');
    const [statusFilter, setStatusFilter] = useState(initialStatusFilter);
    const [search, setSearch] = useState('');
    const [customerNameFilter, setCustomerNameFilter] = useState('');
    const initialMonthRange = useMemo(() => getCurrentMonthRange(), []);
    const [fromDate, setFromDate] = useState(initialMonthRange.fromDate);
    const [toDate, setToDate] = useState(initialMonthRange.toDate);
    const [workingBatchId, setWorkingBatchId] = useState(null);
    const [bulkApproving, setBulkApproving] = useState(false);
    const [exportingExcel, setExportingExcel] = useState(false);
    const showBankSplitSummary = ['exported', 'ready_for_bank_upload', 'pending_finance_review', 'submitted_to_bank'].includes(statusFilter);

    const loadRefunds = async () => {
        const params = new URLSearchParams();
        if (statusFilter === 'finance') {
            params.append('ticketStatus', 'in_progress');
        } else if (statusFilter) {
            params.append('status', statusFilter);
        }
        if (statusFilter === 'approved') {
            if (fromDate) params.append('fromDate', fromDate);
            if (toDate) params.append('toDate', toDate);
        }
        const response = await handleGetRequest(endPoints.getRefundQueue(params.toString()), true, false, dispatch);
        setRefunds(Array.isArray(response?.data) ? response.data : []);
    };

    useEffect(() => {
        loadRefunds();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [statusFilter, fromDate, toDate]);

    useEffect(() => {
        const nextParams = new URLSearchParams(searchParams);
        nextParams.delete('status');
        nextParams.delete('ticketStatus');
        if (statusFilter === 'finance') {
            nextParams.set('ticketStatus', 'in_progress');
        } else if (statusFilter) {
            nextParams.set('status', statusFilter);
        }
        setSearchParams(nextParams, { replace: true });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [statusFilter]);

    const filteredRefunds = useMemo(() => {
        const term = normalizeSearchText(search);
        const customerTerm = normalizeSearchText(customerNameFilter);
        return refunds.filter((item) => {
            const refund = item?.refund || {};
            const customerName = refund.customerName || item?.manualIntake?.customerName || '';
            const searchable = [
                item?._id,
                item?.whatsAppNumber,
                customerName,
                refund.accountNumber,
                refund.batchId,
                item?.machine?.location,
                item?.machine?.serialNumber,
                item?.machine?.deviceId
            ]
                .filter(Boolean)
                .join(' ')
                .normalize('NFD')
                .replace(/[\u0300-\u036f]/g, '')
                .toLowerCase();
            const matchesSearch = !term || searchable.includes(term);
            if (!matchesSearch) return false;

            const matchesCustomerName = !customerTerm || normalizeSearchText(customerName).includes(customerTerm);
            if (statusFilter === 'approved' && !matchesCustomerName) return false;

            if (statusFilter !== 'approved') return true;
            if (!fromDate && !toDate) return true;

            const approvedDateKey = toDateKey(refund?.approvedAt || item?.updatedAt || item?.createdAt);
            if (!approvedDateKey) return false;

            if (fromDate && approvedDateKey < fromDate) return false;
            if (toDate && approvedDateKey > toDate) return false;
            return true;
        });
    }, [refunds, search, customerNameFilter, statusFilter, fromDate, toDate]);

    const batchIds = useMemo(() => {
        return Array.from(new Set(refunds.map((item) => item?.refund?.batchId).filter(Boolean)));
    }, [refunds]);

    const exportedTotalsByBank = useMemo(() => {
        const initial = {
            bancoGeneral: { count: 0, amount: 0 },
            other: { count: 0, amount: 0 }
        };

        filteredRefunds.forEach((item) => {
            const refund = item?.refund || {};
            const amount = toAmount(refund.amount);
            const normalizedBank = String(refund.bankName || '').trim().toLowerCase();
            const isBancoGeneral = normalizedBank === 'banco general';
            if (isBancoGeneral) {
                initial.bancoGeneral.count += 1;
                initial.bancoGeneral.amount += amount;
            } else {
                initial.other.count += 1;
                initial.other.amount += amount;
            }
        });

        return initial;
    }, [filteredRefunds]);

    const approvedTotals = useMemo(() => {
        const initial = {
            bancoGeneral: { count: 0, amount: 0 },
            other: { count: 0, amount: 0 },
            total: { count: 0, amount: 0 }
        };

        filteredRefunds.forEach((item) => {
            const refund = item?.refund || {};
            const amount = toAmount(refund.amount);
            const normalizedBank = String(refund.bankName || '').trim().toLowerCase();
            const isBancoGeneral = normalizedBank === 'banco general';
            if (isBancoGeneral) {
                initial.bancoGeneral.count += 1;
                initial.bancoGeneral.amount += amount;
            } else {
                initial.other.count += 1;
                initial.other.amount += amount;
            }
            initial.total.count += 1;
            initial.total.amount += amount;
        });

        return initial;
    }, [filteredRefunds]);

    const downloadTextFile = (fileName, content) => {
        const blob = new Blob([content], { type: 'text/plain;charset=utf-8' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = fileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
    };

    const exportApprovedRefundsToExcel = async () => {
        if (!filteredRefunds.length || exportingExcel) return;

        setExportingExcel(true);
        try {
            const xlsxModule = await import('xlsx');
            const XLSX = xlsxModule.default || xlsxModule;
            const rows = filteredRefunds.map((item) => {
                const refund = item?.refund || {};
                return {
                    [t('Date')]: toDateKey(refund.approvedAt || item?.updatedAt || item?.createdAt),
                    [t('Customer')]: refund.customerName || item?.manualIntake?.customerName || '',
                    [t('WhatsApp Number')]: item?.whatsAppNumber || '',
                    [t('Amount')]: toAmount(refund.amount),
                    Moneda: refund.currency || 'USD',
                    [t('Bank Entity')]: refund.bankName || 'Banco General',
                    [t('Account Type')]: formatRefundAccountType(refund.accountType),
                    [t('Account Number')]: refund.accountNumber || '',
                    [t('Batch')]: refund.batchId || '',
                    [t('Status')]: t(formatStatus(refund.status)),
                    [t('Complaint ID')]: item?._id || '',
                    [t('Location')]: item?.machine?.location || '',
                    [t('Serial Number')]: item?.machine?.serialNumber || '',
                    [t('Device ID')]: item?.machine?.deviceId || '',
                    Referencia: refund.transactionReference || ''
                };
            });

            const worksheet = XLSX.utils.json_to_sheet(rows);
            worksheet['!cols'] = [{ wch: 12 }, { wch: 28 }, { wch: 18 }, { wch: 12 }, { wch: 10 }, { wch: 22 }, { wch: 16 }, { wch: 22 }, { wch: 24 }, { wch: 20 }, { wch: 26 }, { wch: 30 }, { wch: 20 }, { wch: 18 }, { wch: 22 }];
            const workbook = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(workbook, worksheet, 'Reembolsos');
            XLSX.writeFile(workbook, `reembolsos_aprobados_${fromDate || 'inicio'}_${toDate || 'hoy'}.xlsx`, { compression: true });
        } finally {
            setExportingExcel(false);
        }
    };

    const exportReadyRefunds = async () => {
        const response = await dispatch(handlePostRequest({}, endPoints.exportRefundBatch, true, true));
        const payload = response?.data?.data;
        if (response?.status === 200 && payload) {
            if (Array.isArray(payload.files) && payload.files.length) {
                payload.files.forEach((file) => {
                    if (file?.content) {
                        downloadTextFile(file.fileName || `${payload.batchId}.txt`, file.content);
                    }
                });
            } else if (payload?.content) {
                downloadTextFile(payload.fileName || `${payload.batchId}.txt`, payload.content);
            }
            setWorkingBatchId(payload.batchId);
            await loadRefunds();
        }
    };

    const updateRefundStatus = async (complaintId, status) => {
        const response = await dispatch(handlePatchRequest({ status }, endPoints.updateComplaintRefund(complaintId), true, true));
        if (response?.status === 200) {
            await loadRefunds();
        }
    };

    const markRefundAlreadyProcessed = async (complaintId) => {
        const note = window.prompt(t('Already processed note'), t('Refund was previously approved and processed'));
        if (!note) return;
        const response = await dispatch(
            handlePatchRequest(
                { note },
                endPoints.markComplaintRefundAlreadyProcessed(complaintId),
                true,
                true
            )
        );
        if (response?.status === 200) {
            await loadRefunds();
        }
    };

    const approveAllPendingVisible = async () => {
        const targetRefunds = filteredRefunds.filter((item) => item?.refund?.status === 'pending_finance_review');
        if (!targetRefunds.length || bulkApproving) return;
        const confirmed = window.confirm(t('Approve all pending refunds confirmation'));
        if (!confirmed) return;

        setBulkApproving(true);
        try {
            await Promise.all(
                targetRefunds.map((item) =>
                    dispatch(
                        handlePatchRequest(
                            { status: 'ready_for_bank_upload' },
                            endPoints.updateComplaintRefund(item._id),
                            true,
                            true
                        )
                    )
                )
            );
            await loadRefunds();
        } finally {
            setBulkApproving(false);
        }
    };

    const markBatchSubmitted = async (batchId) => {
        if (!batchId) return;
        const response = await dispatch(handlePatchRequest({}, endPoints.submitRefundBatch(batchId), true, true));
        if (response?.status === 200) {
            setWorkingBatchId(batchId);
            await loadRefunds();
        }
    };

    const approveBatch = async (batchId) => {
        if (!window.confirm("Confirme que verificó los pagos de este lote. Se marcarán como aprobados, se notificará a los clientes y se cerrarán los reclamos que no tengan otras gestiones pendientes.")) return;
        if (!batchId) return;
        const response = await dispatch(handlePatchRequest({}, endPoints.approveRefundBatch(batchId), true, true));
        if (response?.status === 200) {
            setWorkingBatchId(batchId);
            await loadRefunds();
        }
    };

    const reopenExportedBatch = async (batchId) => {
        if (!batchId) return;
        const confirmed = window.confirm(t('Send batch back to ready for bank upload confirmation'));
        if (!confirmed) return;
        const response = await dispatch(handlePatchRequest({}, endPoints.reopenRefundBatch(batchId), true, true));
        if (response?.status === 200) {
            setWorkingBatchId(null);
            await loadRefunds();
        }
    };

    const refundStatusBody = (row) => <Tag severity={statusSeverity(row?.refund?.status)} value={t(formatStatus(row?.refund?.status))} />;

    const amountBody = (row) => {
        const refund = row?.refund || {};
        const amount = Number(refund.amount || 0);
        return `${refund.currency || 'USD'} ${amount.toFixed(2)}`;
    };

    const duplicateBody = (row) => {
        const warnings = row?.refund?.duplicateWarnings || [];
        const recurrenceCount = row?.refund?.recurrenceCount
            ?? ((row?.refund?.duplicateCount ?? warnings.length) > 0
                ? (row?.refund?.duplicateCount ?? warnings.length) + 1
                : 0);
        return recurrenceCount ? <Tag severity="warning" value={recurrenceCount} /> : '-';
    };

    const refundCommentsBody = (row) => {
        const comments = getRefundCommentsForDisplay(row?.refund);
        const latestComment = comments.length ? comments[comments.length - 1] : null;
        const text = latestComment?.text || '';
        if (!text) return '-';

        return (
            <div style={{ maxWidth: '18rem' }}>
                <div className="text-sm white-space-normal">{text}</div>
                {latestComment ? (
                    <div className="text-xs text-500 mt-1">
                        {formatAdminUserName(latestComment.createdBy) || t('Unknown user')}
                        {latestComment.legacyReason ? ` - ${t('Saved refund reason')}` : ''}
                        {latestComment.refundAudit ? ` - ${t('Refund history')}` : ''}
                    </div>
                ) : null}
            </div>
        );
    };

    const actionsBody = (row) => {
        const status = row?.refund?.status;
        const audit = Array.isArray(row?.refund?.audit) ? row.refund.audit : [];
        const hasPriorApproval = audit.some(
            (entry) => entry?.status === 'approved' || String(entry?.action || '').includes('refund_approved')
        );
        const manual = row?.refund?.manualManagement;
        const bankLocked = Boolean(row?.refund?.batchId || row?.refund?.submittedAt || row?.refund?.approvedAt || hasPriorApproval || audit.some(a => ['exported', 'submitted_to_bank'].includes(a.status)));
        const actionButton = (action, label, style = 'p-button-secondary') => <Button label={label} className={`p-button-sm ${style}`} onClick={() => setFinanceDialog({ action, complaint: row })} />;
        const canReject = [
            'pending_finance_review',
            'ready_for_bank_upload',
            'revision',
            'exported',
            'submitted_to_bank'
        ].includes(status);
        return (
            <div className="flex gap-2 flex-wrap">
                {canOpenComplaintDetail ? (
                    <Button
                        icon="pi pi-comments"
                        className="p-button-rounded p-button-text p-button-sm"
                        tooltip={t('Open complaint')}
                        onClick={() =>
                            navigate('/complaints/chat', {
                                state: {
                                    complaint: row,
                                    returnTo: `${location.pathname}${location.search}`
                                }
                            })
                        }
                    />
                ) : null}
                {!bankLocked && ['pending_finance_review', 'revision', 'manual_pending'].includes(status) ? (
                    <FinanceReadyButton complaintId={row._id} label={t('Ready')} onSaved={loadRefunds} />
                ) : null}
                {status === 'exported' ? (
                    <Button label={t('Revision')} icon="pi pi-exclamation-circle" className="p-button-sm p-button-warning" onClick={() => updateRefundStatus(row._id, 'revision')} />
                ) : null}
                {hasPriorApproval && status !== 'approved' ? (
                    <Button
                        label={t('Already processed')}
                        icon="pi pi-history"
                        className="p-button-sm p-button-secondary"
                        tooltip={t('Restore the prior approval without notifying the customer')}
                        onClick={() => markRefundAlreadyProcessed(row._id)}
                    />
                ) : null}
                {!bankLocked && status === 'rejected' && actionButton('reopen_rejected', 'Corregir rechazo')}
                {!bankLocked && ['pending_finance_review', 'ready_for_bank_upload', 'manual_pending'].includes(status) && actionButton('request_correction', 'Solicitar corrección', 'p-button-warning')}
                {status === 'approved' && manual?.recordedAt && ['pending', 'failed'].includes(manual.notificationStatus) && actionButton('notify_manual_payment', 'Reintentar notificación')}
                {canReject ? (
                    <Button icon="pi pi-times" className="p-button-rounded p-button-danger p-button-sm" tooltip={t('Rechazar definitivamente')} onClick={() => setFinanceDialog({ action: 'reject', complaint: row })} />
                ) : null}
            </div>
        );
    };

    return (
        <div className="p-4">
            <FinanceRefundDialog action={financeDialog?.action} complaint={financeDialog?.complaint} onHide={() => setFinanceDialog(null)} onSaved={(result) => {
                setFinanceDialog(null);
                setFinanceNotice(['failed', 'unknown'].includes(result.notificationStatus) ? 'El pago quedó registrado. La notificación no está confirmada; revise su estado antes de reintentar. No registre otro pago.' : 'Acción registrada.');
                loadRefunds();
            }} />
            {financeNotice && <p role="status">{financeNotice}</p>}
            <div className="flex justify-content-between align-items-center gap-3 mb-3 flex-wrap">
                <div>
                    <h3 className="m-0">{t('Refund Queue')}</h3>
                    <div className="text-sm text-600 mt-1">
                        {filteredRefunds.length} {t(statusFilter === 'finance' ? 'Tickets in Finance' : 'Refunds in this stage')}
                    </div>
                </div>
                <div className="flex gap-2 flex-wrap justify-content-end">
                    <Dropdown value={statusFilter} options={refundStatusOptions.map((item) => ({ ...item, label: t(item.label) }))} onChange={(e) => setStatusFilter(e.value)} className="w-15rem" />
                    <span className="p-input-icon-left">
                        <i className="pi pi-search" />
                        <InputText value={search} onChange={(e) => setSearch(e.target.value)} placeholder={t('Search refunds')} />
                    </span>
                    {statusFilter === 'pending_finance_review' ? (
                        <Button
                            label={t('Approve all pending')}
                            icon="pi pi-check-square"
                            className="p-button-success"
                            onClick={approveAllPendingVisible}
                            disabled={bulkApproving || !filteredRefunds.some((item) => item?.refund?.status === 'pending_finance_review')}
                            loading={bulkApproving}
                        />
                    ) : null}
                    <Button label={t('Export Ready TXT')} icon="pi pi-download" onClick={exportReadyRefunds} disabled={statusFilter && statusFilter !== 'ready_for_bank_upload'} />
                </div>
            </div>

            {statusFilter === 'approved' ? (
                <div className="flex gap-2 align-items-end flex-wrap mb-3">
                    <div className="flex flex-column">
                        <label className="mb-1 text-700">{t('From date')}</label>
                        <InputText
                            type="date"
                            value={fromDate}
                            max={toDate || undefined}
                            onChange={(e) => {
                                const value = e.target.value;
                                setFromDate(value);
                                if (value && toDate && value > toDate) setToDate(value);
                            }}
                        />
                    </div>
                    <div className="flex flex-column">
                        <label className="mb-1 text-700">{t('To date')}</label>
                        <InputText
                            type="date"
                            value={toDate}
                            min={fromDate || undefined}
                            onChange={(e) => {
                                const value = e.target.value;
                                setToDate(value);
                                if (value && fromDate && value < fromDate) setFromDate(value);
                            }}
                        />
                    </div>
                    <div className="flex flex-column">
                        <label className="mb-1 text-700">{preferredLanguage === 'es' ? 'Nombre y apellido' : 'First and last name'}</label>
                        <span className="p-input-icon-left">
                            <i className="pi pi-user" />
                            <InputText
                                value={customerNameFilter}
                                onChange={(e) => setCustomerNameFilter(e.target.value)}
                                placeholder={preferredLanguage === 'es' ? 'Buscar por nombre o apellido' : 'Search by first or last name'}
                            />
                        </span>
                    </div>
                    <Button
                        type="button"
                        className="p-button-text"
                        icon="pi pi-calendar"
                        label={preferredLanguage === 'es' ? 'Mes actual' : 'Current month'}
                        onClick={() => {
                            const currentMonth = getCurrentMonthRange();
                            setFromDate(currentMonth.fromDate);
                            setToDate(currentMonth.toDate);
                        }}
                        disabled={fromDate === initialMonthRange.fromDate && toDate === initialMonthRange.toDate}
                    />
                    <Button type="button" icon="pi pi-file-excel" label={t('Export to Excel')} className="p-button-success" onClick={exportApprovedRefundsToExcel} disabled={!filteredRefunds.length || exportingExcel} loading={exportingExcel} />
                </div>
            ) : null}

            {showBankSplitSummary ? (
                <div className="grid mb-3">
                    <div className="col-12 md:col-6">
                        <div className="p-3 border-1 surface-border border-round surface-card">
                            <div className="text-600 text-sm mb-2">{t('Banco General')}</div>
                            <div className="text-900 font-semibold">
                                {t('Transactions')}: {exportedTotalsByBank.bancoGeneral.count}
                            </div>
                            <div className="text-900 font-semibold">
                                {t('Total Refund Amount')}: USD {exportedTotalsByBank.bancoGeneral.amount.toFixed(2)}
                            </div>
                        </div>
                    </div>
                    <div className="col-12 md:col-6">
                        <div className="p-3 border-1 surface-border border-round surface-card">
                            <div className="text-600 text-sm mb-2">{t('Other')}</div>
                            <div className="text-900 font-semibold">
                                {t('Transactions')}: {exportedTotalsByBank.other.count}
                            </div>
                            <div className="text-900 font-semibold">
                                {t('Total Refund Amount')}: USD {exportedTotalsByBank.other.amount.toFixed(2)}
                            </div>
                        </div>
                    </div>
                </div>
            ) : null}

            {statusFilter === 'approved' ? (
                <div className="grid mb-3">
                    <div className="col-12 md:col-4">
                        <div className="p-3 border-1 surface-border border-round surface-card">
                            <div className="text-600 text-sm mb-2">{t('Banco General')}</div>
                            <div className="text-900 font-semibold">
                                {t('Transactions')}: {approvedTotals.bancoGeneral.count}
                            </div>
                            <div className="text-900 font-semibold">
                                {t('Total Refund Amount')}: USD {approvedTotals.bancoGeneral.amount.toFixed(2)}
                            </div>
                        </div>
                    </div>
                    <div className="col-12 md:col-4">
                        <div className="p-3 border-1 surface-border border-round surface-card">
                            <div className="text-600 text-sm mb-2">{t('Other')}</div>
                            <div className="text-900 font-semibold">
                                {t('Transactions')}: {approvedTotals.other.count}
                            </div>
                            <div className="text-900 font-semibold">
                                {t('Total Refund Amount')}: USD {approvedTotals.other.amount.toFixed(2)}
                            </div>
                        </div>
                    </div>
                    <div className="col-12 md:col-4">
                        <div className="p-3 border-1 surface-border border-round surface-card">
                            <div className="text-600 text-sm mb-2">{t('Total')}</div>
                            <div className="text-900 font-semibold">
                                {t('Transactions')}: {approvedTotals.total.count}
                            </div>
                            <div className="text-900 font-semibold">
                                {t('Total Refund Amount')}: USD {approvedTotals.total.amount.toFixed(2)}
                            </div>
                        </div>
                    </div>
                </div>
            ) : null}

            {batchIds.length > 0 ? (
                <div className="flex gap-2 align-items-center flex-wrap mb-3">
                    <Dropdown value={workingBatchId} options={batchIds.map((id) => ({ label: id, value: id }))} onChange={(e) => setWorkingBatchId(e.value)} placeholder={t('Select Batch')} className="w-20rem" />
                    {statusFilter === 'exported' ? (
                        <Button
                            label={t('Send back to Ready for Bank Upload')}
                            icon="pi pi-refresh"
                            className="p-button-warning"
                            disabled={!workingBatchId}
                            onClick={() => reopenExportedBatch(workingBatchId)}
                        />
                    ) : null}
                    {statusFilter === 'exported' ? (
                        <Button
                            label={t('Mark Submitted to Bank')}
                            icon="pi pi-upload"
                            className="p-button-info"
                            disabled={!workingBatchId}
                            onClick={() => markBatchSubmitted(workingBatchId)}
                        />
                    ) : null}
                    {statusFilter === 'submitted_to_bank' ? (
                        <Button
                            label={t('Confirmar pagos del lote y notificar')}
                            icon="pi pi-send"
                            className="p-button-success"
                            disabled={!workingBatchId}
                            onClick={() => approveBatch(workingBatchId)}
                        />
                    ) : null}
                </div>
            ) : null}

            <DataTable
                value={filteredRefunds}
                paginator
                rows={15}
                rowsPerPageOptions={[10, 15, 25]}
                paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
                currentPageReportTemplate={t('Showing {first} to {last} of {totalRecords} Records')}
                responsiveLayout="scroll"
                emptyMessage={t('No refunds found.')}
                className="p-datatable-sm"
            >
                {statusFilter === 'approved' ? <Column header={t('Date')} body={(row) => toDateKey(row?.refund?.approvedAt || row?.updatedAt || row?.createdAt) || '-'} sortable /> : null}
                <Column header={t('Customer')} body={(row) => row?.refund?.customerName || row?.whatsAppNumber || '-'} sortable />
                <Column header={t('Amount')} body={amountBody} sortable />
                <Column header={t('Account Type')} body={(row) => formatRefundAccountType(row?.refund?.accountType)} />

                <Column header={t('Bank Entity')} body={(row) => t(row?.refund?.bankName || 'Banco General')} />
                <Column header={t('Account Number')} body={(row) => row?.refund?.accountNumber || '-'} />
                <Column header={t('Batch')} body={(row) => row?.refund?.batchId || '-'} />
                <Column header={t('Status')} body={refundStatusBody} />
                <Column header={t('Refund Comments')} body={refundCommentsBody} />
                <Column header={t('Recurrence')} body={duplicateBody} />
                <Column header={t('Actions')} body={actionsBody} />
            </DataTable>
        </div>
    );
};

export default RefundQueue;
